import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraOverlayDrawer, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { portfolioClient } from '../../../lib/connect/clients';
import { useLandscapeComparison, useLandscapeIntelligence, useLandscapeStrategyKey, useLandscapeWriteAccess } from '../hooks/useLandscapeStrategy';
import { formatMoney, VALUE_KINDS } from '../portfolioEconomics';
import { MarketObservationEditor } from './MarketObservationEditor';
const T = Testids.landscapeStrategy;
export const permissionDenied = (error)=>error instanceof ConnectError && error.code === Code.PermissionDenied;
const date = (value)=>value?.slice(0, 10) || 'Date unavailable';
function coverageText(c) {
    if (!c || c.totalCount === BigInt(0)) return 'No coverage';
    return `${c.knownCount} of ${c.totalCount} · ${c.knownCount < c.totalCount ? 'Partial coverage' : 'Complete coverage'}`;
}
export function LandscapeStrategy({ landscapeId }) {
    const key = useLandscapeStrategyKey(landscapeId);
    const identity = JSON.stringify(key);
    const previous = useRef(identity);
    const [, setParams] = useSearchParams();
    useLayoutEffect(()=>{
        if (previous.current === identity) return;
        previous.current = identity;
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const k of [
                'comparison',
                'kind',
                'currency',
                'source'
            ])next.delete(k);
            return next;
        }, {
            replace: true
        });
    }, [
        identity,
        setParams
    ]);
    if (!key[2] || !key[3]) return /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        "data-testid": T.unavailable,
        children: "Landscape unavailable"
    });
    // Remount local drafts, selection and mutation observers with their principal.
    return /*#__PURE__*/ _jsx(Strategy, {
        landscapeId: landscapeId
    }, identity);
}
function Strategy({ landscapeId }) {
    const comparison = useLandscapeComparison(landscapeId);
    const intelligence = useLandscapeIntelligence(landscapeId);
    const write = useLandscapeWriteAccess().allowed && !!comparison.data && !comparison.error;
    if (intelligence.error instanceof ConnectError && [
        Code.NotFound,
        Code.PermissionDenied
    ].includes(intelligence.error.code)) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "p",
            "data-testid": T.unavailable,
            children: "Landscape unavailable"
        });
    }
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": T.panel,
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(Intelligence, {
                landscapeId: landscapeId,
                write: write
            }),
            /*#__PURE__*/ _jsx(SpecraCard, {
                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            children: "Current versus market"
                        }),
                        comparison.isLoading && /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            "aria-live": "polite",
                            children: "Loading comparisons…"
                        }),
                        comparison.error && /*#__PURE__*/ _jsxs("div", {
                            role: "alert",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    children: permissionDenied(comparison.error) ? 'You do not have permission to view economics or comparisons.' : 'Comparisons could not be loaded.'
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    onClick: ()=>comparison.refetch(),
                                    children: "Retry comparisons"
                                })
                            ]
                        }),
                        comparison.data && !comparison.error && /*#__PURE__*/ _jsx(Comparison, {
                            landscapeId: landscapeId,
                            write: write
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Hypothesis, {
                landscapeId: landscapeId,
                write: write
            })
        ]
    });
}
function Intelligence({ landscapeId, write }) {
    const query = useLandscapeIntelligence(landscapeId);
    return /*#__PURE__*/ _jsx(SpecraCard, {
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: "Shared intelligence"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    tone: "muted",
                    children: "Released source summaries. Analyst interpretation is recorded separately below."
                }),
                query.isLoading && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    "aria-live": "polite",
                    children: "Loading shared intelligence…"
                }),
                query.error && /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    children: [
                        "Shared intelligence could not be loaded.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>query.refetch(),
                            children: "Retry intelligence"
                        })
                    ]
                }),
                query.data?.intelligence.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    children: "No shared intelligence associated. Associate a permitted release to read it here."
                }),
                !query.error && query.data?.intelligence.map((item)=>/*#__PURE__*/ _jsxs("article", {
                        "data-testid": T.intelligenceRow,
                        "data-release-id": item.releaseId,
                        className: "space-y-1 border-b border-border py-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                children: item.title
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                children: item.summary
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                children: [
                                    "Version ",
                                    item.version.toString(),
                                    " ·",
                                    ' ',
                                    item.status.toLowerCase() === 'current' ? 'Current' : item.status,
                                    " · As of",
                                    ' ',
                                    date(item.reviewedAt),
                                    " · Released ",
                                    date(item.releasedAt)
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                children: [
                                    "Provenance: ",
                                    item.provenance
                                ]
                            })
                        ]
                    }, item.releaseId)),
                write && /*#__PURE__*/ _jsx(IntelligencePicker, {
                    landscapeId: landscapeId
                })
            ]
        })
    });
}
function IntelligencePicker({ landscapeId }) {
    const query = useLandscapeIntelligence(landscapeId, true);
    const cache = useQueryClient();
    const key = useLandscapeStrategyKey(landscapeId);
    const [picked, setPicked] = useState('');
    const mutation = useMutation({
        mutationFn: async ()=>{
            const item = query.data?.intelligence.find((i)=>i.releaseId === picked);
            if (!item) throw new Error('Choose a release');
            await portfolioClient.associateLandscapeIntelligence({
                landscapeId,
                libraryItemId: item.libraryItemId,
                releaseId: item.releaseId
            });
        },
        onSuccess: async ()=>{
            setPicked('');
            await cache.invalidateQueries({
                queryKey: key
            });
        }
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            query.isLoading && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                "aria-live": "polite",
                children: "Loading permitted releases…"
            }),
            query.error && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Releases unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>query.refetch(),
                        children: "Retry releases"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Permitted intelligence release",
                "data-testid": T.intelligencePicker,
                value: picked,
                onChange: (e)=>setPicked(e.target.value),
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "Choose a permitted release…"
                    }),
                    !query.error && query.data?.intelligence.map((i)=>/*#__PURE__*/ _jsx("option", {
                            value: i.releaseId,
                            children: i.title
                        }, i.releaseId))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.intelligenceAssociate,
                disabled: !picked || mutation.isPending,
                isLoading: mutation.isPending,
                onClick: ()=>mutation.mutate(),
                children: "Associate release"
            }),
            mutation.error && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                role: "alert",
                children: "Could not associate this release. Refresh the permitted releases and retry."
            })
        ]
    });
}
function Hypothesis({ landscapeId, write }) {
    const key = useLandscapeStrategyKey(landscapeId);
    const query = useQuery({
        queryKey: [
            ...key,
            'hypotheses'
        ],
        queryFn: ()=>portfolioClient.getLandscapeHypotheses({
                landscapeId
            }),
        retry: false
    });
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState('');
    const [history, setHistory] = useState(false);
    const request = useRef();
    const busy = useRef(false);
    const versions = [
        ...!query.error ? query.data?.versions ?? [] : []
    ].sort((a, b)=>a.version > b.version ? -1 : 1);
    const latest = versions[0];
    const mutation = useMutation({
        mutationFn: async ()=>{
            const revision = latest?.version ?? BigInt(0);
            if (!request.current || request.current.text !== text) request.current = {
                text,
                revision,
                id: crypto.randomUUID()
            };
            return portfolioClient.appendLandscapeHypothesis({
                landscapeId,
                hypothesis: text,
                expectedRevision: request.current.revision,
                requestId: request.current.id
            });
        },
        onSuccess: async (response)=>{
            if (response.version) {
                queryClient.setQueryData([
                    ...key,
                    'hypotheses'
                ], {
                    versions: [
                        response.version,
                        ...versions.filter((v)=>v.id !== response.version?.id)
                    ]
                });
            }
            setEditing(false);
            request.current = undefined;
        },
        onSettled: ()=>{
            busy.current = false;
        }
    });
    const queryClient = useQueryClient();
    return /*#__PURE__*/ _jsx(SpecraCard, {
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: "Strategy hypothesis (optional)"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    children: "Analyst judgement · Interpretation only; does not change source facts or economics."
                }),
                query.isLoading && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    "aria-live": "polite",
                    children: "Loading hypothesis…"
                }),
                query.error && /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    children: [
                        permissionDenied(query.error) ? 'You do not have permission to view analyst judgement.' : 'Hypothesis could not be loaded.',
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>query.refetch(),
                            children: "Retry hypothesis"
                        })
                    ]
                }),
                query.data && !latest && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    "data-testid": T.hypothesisEmpty,
                    children: "No strategy hypothesis. This is optional."
                }),
                latest && /*#__PURE__*/ _jsxs("article", {
                    "data-testid": T.hypothesisCurrent,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            children: [
                                "Analyst judgement · Version ",
                                latest.version.toString()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            children: latest.hypothesis
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "meta",
                            children: [
                                latest.createdBy,
                                " · ",
                                date(latest.createdAt)
                            ]
                        })
                    ]
                }),
                write && query.data && !query.error && !editing && /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.hypothesisEdit,
                    onClick: ()=>{
                        setText(latest?.hypothesis ?? '');
                        mutation.reset();
                        setEditing(true);
                    },
                    children: "Edit hypothesis"
                }),
                editing && write && !query.error && /*#__PURE__*/ _jsxs("form", {
                    "data-testid": T.hypothesisEditor,
                    className: "space-y-2",
                    onSubmit: (e)=>{
                        e.preventDefault();
                        if (busy.current || !text.trim()) return;
                        busy.current = true;
                        mutation.mutate();
                    },
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            children: "Analyst judgement"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            "aria-label": "Analyst judgement",
                            "data-testid": T.hypothesisInput,
                            value: text,
                            onChange: (e)=>setText(e.target.value),
                            disabled: mutation.isPending
                        }),
                        mutation.error && /*#__PURE__*/ _jsxs("div", {
                            role: "alert",
                            children: [
                                mutation.error instanceof ConnectError && mutation.error.code === Code.Aborted ? 'Another analyst saved a newer version. Reload latest, review it alongside your draft, then retry.' : 'Could not save. Your draft is retained; retry saving.',
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    onClick: ()=>{
                                        request.current = undefined;
                                        query.refetch();
                                    },
                                    children: "Reload latest"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "submit",
                            "data-testid": T.hypothesisSave,
                            disabled: !text.trim() || mutation.isPending,
                            isLoading: mutation.isPending,
                            children: "Save hypothesis"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "button",
                            disabled: mutation.isPending,
                            variant: "ghost",
                            onClick: ()=>setEditing(false),
                            children: "Cancel"
                        })
                    ]
                }),
                latest && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    "data-testid": T.hypothesisHistoryOpen,
                    onClick: ()=>setHistory(!history),
                    children: "Version history"
                }),
                history && !query.error && /*#__PURE__*/ _jsx("div", {
                    "data-testid": T.hypothesisHistory,
                    className: "space-y-3",
                    children: versions.map((v)=>/*#__PURE__*/ _jsxs("article", {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        "Version ",
                                        v.version.toString(),
                                        " · ",
                                        v.createdBy,
                                        " · ",
                                        date(v.createdAt)
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    children: v.hypothesis
                                })
                            ]
                        }, v.id || v.version.toString()))
                })
            ]
        })
    });
}
function Comparison({ landscapeId, write }) {
    const { data } = useLandscapeComparison(landscapeId);
    const [params, setParams] = useSearchParams();
    const [adding, setAdding] = useState(false);
    if (!data) return null;
    const side = params.get('comparison');
    const kind = params.get('kind');
    const currency = params.get('currency');
    const sourceId = params.get('source');
    const sources = (side === 'current' ? data.currentSources.filter((s)=>s.kind === kind && s.currency === currency).map((s)=>({
            id: s.id,
            opportunity_id: s.opportunityId,
            lot_id: s.lotId || null,
            amount: s.amount,
            currency: s.currency,
            date: s.date,
            date_field: s.dateField,
            provenance: s.provenance
        })) : data.marketObservations.filter((s)=>s.currency === currency && s.amount !== undefined).map((s)=>({
            id: s.id,
            amount: s.amount ?? '',
            currency: s.currency,
            date: s.asOf,
            date_field: 'as_of',
            provenance: s.provenance
        }))).sort((a, b)=>a.id.localeCompare(b.id));
    const selected = sources.find((s)=>s.id === sourceId);
    const coverage = (side === 'current' ? data.currentCoverage : data.marketCoverage).find((c)=>c.kind === kind && c.currency === currency);
    const close = ()=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const k of [
                'comparison',
                'kind',
                'currency',
                'source'
            ])next.delete(k);
            return next;
        });
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": T.comparisons,
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                tone: "muted",
                children: "Current dates are record updates (updated_at), not historical source periods. Market dates are observation as-of dates. Recorded market estimates may overlap; sums are not total market size or actual revenue."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                children: [
                    'current',
                    'market'
                ].map((s)=>/*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                children: s === 'current' ? 'Current canonical economics' : 'Recorded market estimates'
                            }),
                            (s === 'current' ? data.currentCoverage : data.marketCoverage).map((c)=>{
                                const total = s === 'current' ? data.current?.[VALUE_KINDS.find((k)=>k.key === c.kind)?.key ?? 'deal'].find((t)=>t.currency === c.currency) : data.marketTotals.find((t)=>t.currency === c.currency);
                                return /*#__PURE__*/ _jsxs(SpecraButton, {
                                    variant: "outline",
                                    className: "h-auto whitespace-normal",
                                    "data-testid": T.figure,
                                    "data-side": s,
                                    "data-kind": c.kind,
                                    "data-currency": c.currency,
                                    onClick: ()=>setParams((prev)=>{
                                            const next = new URLSearchParams(prev);
                                            next.set('comparison', s);
                                            next.set('kind', c.kind);
                                            next.set('currency', c.currency);
                                            next.delete('source');
                                            return next;
                                        }),
                                    children: [
                                        s === 'current' ? VALUE_KINDS.find((k)=>k.key === c.kind)?.label ?? c.kind : 'Recorded estimates',
                                        ' ',
                                        "· ",
                                        c.currency,
                                        " · ",
                                        total ? formatMoney(total) : 'Unknown amount',
                                        " ·",
                                        ' ',
                                        coverageText(c),
                                        " · As of:",
                                        ' ',
                                        s === 'current' ? 'record update dates' : 'observation dates',
                                        " (see sources)"
                                    ]
                                }, `${c.kind}:${c.currency}`);
                            }),
                            s === 'current' && data.currentCoverage.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                children: data.currentOpportunityCount === BigInt(0) ? 'No current opportunities. No coverage.' : `${data.currentOpportunityCount} current opportunities; amounts unknown. No coverage.`
                            }),
                            s === 'market' && data.marketObservations.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                children: "No market observations. Record an observation to compare dated estimates."
                            })
                        ]
                    }, s))
            }),
            data.marketObservations.map((o)=>/*#__PURE__*/ _jsxs("article", {
                    "data-testid": T.marketRow,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            children: [
                                o.title,
                                " ·",
                                ' ',
                                o.amount === undefined ? 'Unknown amount' : formatMoney({
                                    amount: o.amount,
                                    currency: o.currency
                                }),
                                ' ',
                                "· ",
                                o.currency
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "meta",
                            children: [
                                "As of ",
                                o.asOf,
                                " · Provenance: ",
                                o.provenance
                            ]
                        })
                    ]
                }, o.id)),
            write && /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.marketAdd,
                onClick: ()=>setAdding(true),
                children: "Add market observation"
            }),
            adding && /*#__PURE__*/ _jsx(MarketObservationEditor, {
                landscapeId: landscapeId,
                onClose: ()=>setAdding(false)
            }),
            /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
                open: !!side,
                onOpenChange: (open)=>{
                    if (!open) close();
                },
                title: selected ? 'Source detail' : 'Dated contributing sources',
                description: `${side} · ${kind} · ${currency}`,
                contentProps: {
                    'data-testid': selected ? T.sourceDetail : T.drilldown,
                    'data-side': side ?? '',
                    'data-source-id': selected?.id
                },
                footer: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.drillClose,
                    onClick: close,
                    children: "Close sources"
                }),
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: coverageText(coverage)
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: side === 'current' ? 'Record updated_at dates; not historical source periods.' : 'Observation as_of dates.'
                    }),
                    (selected ? [
                        selected
                    ] : sources).map((s)=>/*#__PURE__*/ _jsxs("article", {
                            "data-testid": selected ? undefined : T.sourceRow,
                            "data-source-json": JSON.stringify(s),
                            className: "space-y-2 border-b border-border py-3",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        formatMoney({
                                            amount: s.amount,
                                            currency: s.currency
                                        }),
                                        ' ',
                                        "· ",
                                        s.currency
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        s.date,
                                        " · ",
                                        s.date_field,
                                        " · ",
                                        s.provenance
                                    ]
                                }),
                                !selected && /*#__PURE__*/ _jsx(Link, {
                                    "data-testid": T.sourceLink,
                                    to: `?${new URLSearchParams({
                                        ...Object.fromEntries(params),
                                        source: s.id
                                    })}`,
                                    children: "View source details"
                                }),
                                selected && 'opportunity_id' in s && /*#__PURE__*/ _jsx(Link, {
                                    to: `/opportunities?watchOpportunity=${encodeURIComponent(String(s.opportunity_id))}`,
                                    children: "Open owning Opportunity"
                                })
                            ]
                        }, s.id)),
                    sources.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: "No known contributing amounts. Unknown amounts are not zero."
                    })
                ]
            })
        ]
    });
}
